<template>
  <div>
    <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>
    <div class="loading" v-if="fetchData"><load-data></load-data></div>
    <div v-else-if="!logs.length" class="text-muted small py-2">No edits or deletions recorded.</div>
    <template v-else>
      <div class="d-flex mb-2">
        <b-form-input v-model="filterQuestionNo" size="sm" placeholder="Filter by Q.No" style="max-width:160px" class="mr-2" />
        <b-form-select v-model="filterAction" size="sm" :options="actionOptions" style="max-width:160px" />
      </div>
      <table class="table table-bordered table-sm mb-0">
        <thead class="thead-dark">
          <tr>
            <th style="width:160px">When</th>
            <th style="width:150px">By</th>
            <th style="width:70px" class="text-center">Q.No</th>
            <th style="width:90px">Action</th>
            <th>Changes</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="log in filteredLogs">
            <tr :key="log.id">
              <td>{{ formatDateTime(log.created_at) }}</td>
              <td>{{ log.user_name || "-" }}</td>
              <td class="text-center">{{ log.question_no }}</td>
              <td>
                <b-badge :variant="log.action === 'deleted' ? 'danger' : 'info'">
                  {{ log.action === "deleted" ? "Deleted" : "Edited" }}
                </b-badge>
              </td>
              <td>
                <template v-if="log.action === 'deleted'">
                  <a href="#" class="text-primary" @click.prevent="toggle(log.id)">
                    {{ expanded[log.id] ? "Hide question" : "View question" }}
                  </a>
                </template>
                <template v-else>
                  <div v-for="field in changedFields(log)" :key="field">
                    <span class="font-weight-bold">{{ fieldLabel(field) }}:</span>
                    <template v-if="isContentField(field)">
                      <a href="#" class="text-primary ml-1" @click.prevent="toggle(log.id)">
                        {{ expanded[log.id] ? "Hide" : "Compare" }}
                      </a>
                    </template>
                    <template v-else>
                      <span class="text-muted">{{ displayValue(log.old_content[field], field) }}</span>
                      &rarr;
                      <span>{{ displayValue(log.new_content[field], field) }}</span>
                    </template>
                  </div>
                </template>
              </td>
            </tr>
            <tr v-if="expanded[log.id]" :key="log.id + '-detail'" class="log-detail">
              <td colspan="5">
                <!-- deleted: the whole question as it was -->
                <template v-if="log.action === 'deleted'">
                  <div v-for="field in snapshotFields(log.old_content)" :key="field" class="mb-1">
                    <span class="font-weight-bold">{{ fieldLabel(field) }}:</span>
                    <div v-if="isContentField(field)" class="content-box">
                      <content-blocks :blocks="log.old_content[field]" :files="log.old_content._files" :storage-root="storageRoot" />
                    </div>
                    <span v-else class="ml-1">{{ displayValue(log.old_content[field], field) }}</span>
                  </div>
                </template>
                <!-- edited: question text / evaluation scheme side by side -->
                <template v-else>
                  <div v-for="field in changedFields(log).filter(isContentField)" :key="field" class="mb-2">
                    <div class="font-weight-bold mb-1">{{ fieldLabel(field) }}</div>
                    <div class="d-flex">
                      <div class="content-box flex-fill mr-2">
                        <div class="small text-muted mb-1">Before</div>
                        <content-blocks :blocks="log.old_content[field]" :files="log.old_content._files" :storage-root="storageRoot" />
                      </div>
                      <div class="content-box flex-fill">
                        <div class="small text-muted mb-1">After</div>
                        <content-blocks :blocks="log.new_content[field]" :files="log.new_content._files" :storage-root="storageRoot" />
                      </div>
                    </div>
                  </div>
                </template>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import "mathlive";

// Fields that change as a side effect or mean nothing to a reviewer
const HIDDEN_FIELDS = ["id", "exam_id", "institution_id", "created_by", "_files"];

// stored as 1/0
const YES_NO_FIELDS = ["allow_descriptive", "allow_attachment_answer", "answer_required"];

const FIELD_LABELS = {
  question_no: "Question No.",
  name: "Name",
  question: "Question",
  summary: "Summary",
  type: "Type",
  difficulty_level: "Difficulty Level",
  evaluation_scheme: "Evaluation Scheme",
  allow_descriptive: "Allow Descriptive",
  allow_attachment_answer: "Allow Attachment Answer",
  answer_required: "Answer Required",
  maximum_mark: "Max Mark",
  wrong_answer_mark: "Wrong Answer Mark",
  blooms_taxonamy_level: "Bloom's Level",
  section_id: "Section",
  module_id: "Module",
  course_outcomes: "Course Outcomes",
  programme_outcomes: "Programme Outcomes",
  programme_specific_outcomes: "Programme Specific Outcomes",
  subquestion_parent_id: "Parent Question",
  orquestion_group_id: "OR Group",
};

// Renders question/evaluation-scheme blocks: text (HTML with $$latex$$), equation, image
const ContentBlocks = {
  props: ["blocks", "files", "storageRoot"],
  methods: {
    textHtml(value) {
      return String(value || "").replace(/\$\$(.*?)\$\$/g, '<math-field class="p-0" style="border: none;" readonly>$1</math-field>');
    },
    imageUrl(fileId) {
      const path = this.files && this.files[fileId];
      return path ? this.storageRoot + path : null;
    },
  },
  render(h) {
    const blocks = Array.isArray(this.blocks) ? this.blocks : [];
    if (!blocks.length) return h("span", { class: "text-muted" }, "(empty)");
    return h("div", blocks.map((block, index) => {
      if (block.type === "text") {
        return h("span", { key: index, class: "mr-1", domProps: { innerHTML: this.textHtml(block.value) } });
      }
      if (block.type === "equation") {
        return h("math-field", { key: index, class: "p-0 mr-1", style: { border: "none" }, attrs: { readonly: true } }, block.value);
      }
      if (block.type === "image") {
        const url = this.imageUrl(block.value);
        return url
          ? h("img", { key: index, class: "log-image", attrs: { src: url } })
          : h("span", { key: index, class: "text-muted mr-1" }, "[image no longer available]");
      }
      return h("span", { key: index, class: "mr-1" }, String(block.value || ""));
    }));
  },
};

export default {
  name: "ExamQuestionLogs",
  components: { ContentBlocks },
  props: {
    examId: { type: Number, required: true },
  },
  data() {
    return {
      fetchData: false,
      errorMessage: "",
      logs: [],
      expanded: {},
      filterQuestionNo: "",
      filterAction: null,
      actionOptions: [
        { value: null, text: "All actions" },
        { value: "updated", text: "Edited" },
        { value: "deleted", text: "Deleted" },
      ],
      storageRoot: "",
    };
  },
  computed: {
    filteredLogs() {
      const questionNo = this.filterQuestionNo.trim();
      return this.logs.filter(log =>
        (log.action === "deleted" || this.changedFields(log).length) &&
        (!questionNo || String(log.question_no || "").startsWith(questionNo)) &&
        (!this.filterAction || log.action === this.filterAction)
      );
    },
  },
  async mounted() {
    this.storageRoot = this.$store.getters.getAPIKey.userAttachments;
    await this.fetchLogs();
  },
  methods: {
    async fetchLogs() {
      this.fetchData = true;
      this.errorMessage = "";
      try {
        const res = await this.$axios.get(`${this.$store.getters.getAPIKey.examination}/${this.examId}/question-logs`);
        this.logs = res.data || [];
      } catch {
        this.errorMessage = "Failed to load the question log.";
      } finally {
        this.fetchData = false;
      }
    },

    toggle(logId) {
      this.$set(this.expanded, logId, !this.expanded[logId]);
    },

    // skips fields that only changed type (1 → true, "10.0" → "10"); older entries still contain them
    changedFields(log) {
      return Object.keys(log.new_content || {}).filter(field => {
        if (HIDDEN_FIELDS.includes(field)) return false;
        const oldValue = (log.old_content || {})[field];
        const newValue = log.new_content[field];
        if (this.isContentField(field)) return JSON.stringify(oldValue) !== JSON.stringify(newValue);
        return this.displayValue(oldValue, field) !== this.displayValue(newValue, field);
      });
    },

    snapshotFields(content) {
      return Object.keys(content || {}).filter(field =>
        !HIDDEN_FIELDS.includes(field) && content[field] !== null && content[field] !== ""
      );
    },

    isContentField(field) {
      return field === "question" || field === "evaluation_scheme";
    },

    fieldLabel(field) {
      return FIELD_LABELS[field] || field.replace(/_/g, " ");
    },

    displayValue(value, field) {
      if (value === null || value === undefined || value === "") return "(empty)";
      // outcome mappings are lists of codes
      if (Array.isArray(value)) return value.length ? value.join(", ") : "(none)";
      if (YES_NO_FIELDS.includes(field) || typeof value === "boolean") return Number(value) ? "Yes" : "No";
      const number = Number(value);
      if (!Number.isNaN(number)) {
        return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(2)));
      }
      return String(value);
    },

    formatDateTime(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.log-detail {
  background-color: #f8f9fa;
}
.content-box {
  border: 1px solid #dee2e6;
  background-color: #fff;
  padding: 6px 8px;
  border-radius: 3px;
}
.log-image {
  max-width: 240px;
  max-height: 160px;
  margin: 4px;
}
</style>
